/********************************************************************************
 * Copyright (c) 2022 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const { getColIndex, getColIndexFromRef, getRowIndex, getRowIndexFromRef } = require('./refs');

const mapToServerProperties = (type, map) => ({ [type]: map ? { ...map.getMap() } : undefined });

const getCellOrSectionProperties = (obj) =>
	obj == null ? undefined : { attributes: obj.attributes, formats: obj.format, textFormats: obj.textFormat };


const applySectionAttributes = (header, section, attributes = {}) => {
	const { level, size, visible, closed } = attributes;
	// DL-5487 set or reset attributes:
	section.closed = closed == null ? false : closed;
	section.level = level == null || level < 0 ? 0 : level;
	// do not update header sizes while mouse interaction
	if (!header.ignoreSize) {
		section.setVisible(visible == null ? true : visible);
		section.setSize(size == null || (section.getVisible() && size === -1) ? header.defaultSectionSize : size);
	}
};
const applyProperties = (cell, properties = {}) => {
	// an AH: müsste hier kopiert werden?
	cell._properties = properties;
};
const applySectionProperties = (section, properties = {}, header) => {
	section._properties = properties;
	if (properties === null) {
		section.clearFormat();
		// set default values:
		section.level = 0;
		section.closed = false;
		section.setVisible(true);
		section.setSize(header.defaultSectionSize);
	} else {
		const { attributes = {} } = properties || {};
		applyProperties(section, properties);
		applySectionAttributes(header, section, attributes);
	}
};

const cellProvider = (dataprovider) => (ref) => {
	const row = getRowIndexFromRef(ref);
	const column = getColIndexFromRef(ref);
	return dataprovider.create({ x: column, y: row });
};

const columnProvider = (header) => {
	return (ref) => {
		const section = header.getOrCreateSectionAt(getColIndexFromRef(ref));
		// cannot simply return section since it is not connected to its header which is needed
		return { header, section };
	};
};
const rowProvider = (header) => {
	return (ref) => {
		const section = header.getOrCreateSectionAt(getRowIndexFromRef(ref));
		// cannot simply return section since it is not connected to its header which is needed
		return { header, section };
	};
};
const setSectionProperties = (sections, provider, sharedprops) => {
	if (sections && sections.length) {
		sections.forEach(({ ref, properties = sharedprops }) => {
			const { header, section } = provider(ref);
			applySectionProperties(section, properties, header);
		});
	}
};
const setCellProperties = (cells, provider, sharedprops) => {
	if (cells && cells.length) {
		cells.forEach(({ ref, properties = sharedprops }) => {
			const cell = provider(ref);
			applyProperties(cell, properties);
		});
	}
};
const setSheetProperties = (sheet, properties) => {
	if (sheet.defaultCell) applyProperties(sheet.defaultCell, properties);
};

const checkCells = (cells) => {
	const map = cells.reduce((all, cell) => {
		const { ref } = cell;
		const r = getRowIndexFromRef(ref);
		const c = getColIndexFromRef(ref);
		if (!all[r]) all[r] = {};
		all[r][c] = true;
		return all;
	}, {});
	return (col, row) => map[row] && map[row][col];
};
const unsetCellsProperties = (cells, isSet) => {
	cells.enumerate((column, row, cell) => {
		if (!isSet(column, row)) cell.clearFormat();
	});
};
const unset = () => false;
const check = (sections, getIndex) => {
	const map = sections.reduce((all, section) => {
		const idx = getIndex(section);
		if (!all[idx]) all[idx] = true;
		return all;
	}, {});
	return (index) => map[index];
};
const unsetSectionsProperties = (header, isSet) => {
	// do not reset while dragging size
	if (header.ignoreSize) {
		return;
	}
	header.iterateSections((section, index) => {
		if (!isSet(index)) section.reset(header.getSectionDefaults(index));
	});
};

const applyPropertiesDefinitions = (rowData, columnData, cellData, propsdef, sharedprops) => {
	// SERVER_COMMANDS:
	const { sheet, cells, cols, rows } = propsdef;
	const sheetprops = sheet ? sheet.properties || sharedprops : undefined;
	setSheetProperties(cellData.sheet, sheetprops || {});
	setSectionProperties(rows, rowProvider(rowData), sharedprops);
	setSectionProperties(cols, columnProvider(columnData), sharedprops);
	setCellProperties(cells, cellProvider(cellData), sharedprops);
};
const setPropertiesDefinitions = (rowData, columnData, cellData, propsdef, sharedprops) => {
	applyPropertiesDefinitions(rowData, columnData, cellData, propsdef, sharedprops);
	// properties which are not in lists are removed:
	const { cells, cols, rows } = propsdef;
	unsetSectionsProperties(rowData, rows && rows.length ? check(rows, getRowIndex) : unset);
	unsetSectionsProperties(columnData, cols && cols.length ? check(cols, getColIndex) : unset);
	unsetCellsProperties(cellData, cells && cells.length ? checkCells(cells) : unset);
};

const getCellAt = (col, row, cellData) => cellData.getRC(col, row);
const getRowSectionAt = (index, data) => data.getSectionAt(index);
const getColumnSectionAt = (index, data) => data.getSectionAt(index);
const getCellPropertiesAt = (col, row, cellData) => getCellOrSectionProperties(getCellAt(col, row, cellData));
const getRowPropertiesAt = (index, data) => getCellOrSectionProperties(getRowSectionAt(index, data));
const getColumnPropertiesAt = (index, data) => getCellOrSectionProperties(getColumnSectionAt(index, data));

const combineProperties = (...props) => {
	const defined = props.filter((prop) => prop != null);
	const combined = defined.length ? { attributes: {}, formats: {}, textFormats: {} } : undefined;
	defined.forEach((prop) => {
		Object.assign(combined.attributes, prop.attributes);
		Object.assign(combined.formats, prop.formats);
		Object.assign(combined.textFormats, prop.textFormats);
	});
	return combined;
};
const getCombinedCellPropertiesAt = (ref, sheet, cells) => {
	const col = getColIndexFromRef(ref);
	const row = getRowIndexFromRef(ref);
	const defaultProps = getCellOrSectionProperties(sheet.defaultCell);
	const cellprops = getCellPropertiesAt(col, row, cells);
	const rowprops = getRowPropertiesAt(row, sheet.rows);
	const colprops = getColumnPropertiesAt(col, sheet.columns);
	// combine
	return combineProperties(defaultProps, colprops, rowprops, cellprops);
};

module.exports = {
	setPropertiesDefinitions,
	getRowPropertiesAt,
	getColumnPropertiesAt,
	getCombinedCellPropertiesAt,
	mapToServerProperties
};
